/* eslint-disable */
/**
 * AfdelingerPanel.jsx — Panel i Sager-stil til OS-dashboard
 *
 * Datapunkter og status-mærkning:
 *   /api/os/hjerne/afdelinger   → LIVE
 *     Felter pr. agent/afdeling:
 *       agent_id, display_navn, status, aktuel_opgave, sidst_aktiv,
 *       er_aktiv, inaktiv_timer,
 *       afd_kode, afd_navn, division_nr, division_navn,
 *       status_tekst, kpi_data, afventende_opgaver, forbedringsforslag,
 *       rapport_opdateret
 *
 * Opbygning:
 *   - KPI-række: divisioner, afdelinger, forbundne agenter
 *   - Accordeon-liste gruppreret per division (DEL 2's 13 divisioner)
 *     Agenter uden division_nr vises under "Øvrige / ikke kategoriseret"
 *   - Hver afdeling: status, sidst aktiv, kpi_data-nøgler hvis tilgængelig
 *   - TomTilstand for afdelinger der mangler data
 *
 * Regler:
 *   - Ingen hardkodede tal eller afdelingsnavne
 *   - Grupperingsnavne kommer fra division_navn i DB
 *   - TomTilstand hvis endpoint fejler
 */

import React, { useState, useCallback } from 'react';
import {
  KpiKort,
  StatusBadge,
  TomTilstand,
  ModalSkal,
  SektionsHeader,
  useApiPolling,
  STATUS,
  TEKST,
  BG,
  KANT,
  RADIUS,
  FONT,
} from '../komponenter/index';
import { Boks } from '../ui/Boks';

// ─── Hjælpefunktioner ─────────────────────────────────────────────────────────

function formatRelativTid(iso) {
  if (!iso) return 'Aldrig';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1)   return 'Lige nu';
  if (min < 60)  return `${min} min`;
  const t = Math.floor(min / 60);
  if (t < 24)    return `${t}t`;
  const d = Math.floor(t / 24);
  return `${d} dag${d !== 1 ? 'e' : ''}`;
}

// Agent-status mapping (agent-sprog — ikke integration-sprog):
//   er_aktiv=true                           → AKTIV   (grøn)  — kørte for nylig / er i gang
//   er_aktiv=false + sidst_aktiv < 24t     → AKTIV   (grøn)  — kørte inden for tidsgrænsen
//   er_aktiv=false + sidst_aktiv > 24t     → STANDBY (grå)   — ikke kørt for nylig, normalt for on-demand
//   sidst_aktiv = null                      → STANDBY (grå)   — aldrig set, ikke en fejl
//   status = 'FEJLET'                       → FEJL_AGENT (rød) — reel fejl-tilstand
function agentBadge(agent) {
  if (!agent) return 'STANDBY';
  if (agent.status === 'FEJLET') return 'FEJL_AGENT';
  if (agent.er_aktiv) return 'AKTIV';
  if (!agent.sidst_aktiv) return 'STANDBY';
  const timer = (Date.now() - new Date(agent.sidst_aktiv).getTime()) / 3600000;
  return timer < 24 ? 'AKTIV' : 'STANDBY';
}

function agentStatusFarve(agent) {
  if (!agent) return STATUS.ikkeForbundet;
  if (agent.status === 'FEJLET') return STATUS.fejl;
  if (agent.er_aktiv) return STATUS.live;
  if (!agent.sidst_aktiv) return STATUS.ikkeForbundet;
  const timer = (Date.now() - new Date(agent.sidst_aktiv).getTime()) / 3600000;
  return timer < 24 ? STATUS.live : STATUS.ikkeForbundet;
}


// ─── Afdeling-detalje modal ───────────────────────────────────────────────────

function AfdelingDetaljeModal({ agent, onLuk }) {
  if (!agent) return null;
  const badge = agentBadge(agent);
  const farve = agentStatusFarve(agent);

  const felt = (label, value, mono = false) => value == null ? null : (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 14 }}>
      <span style={{ fontSize: 10, color: TEKST.dæmpet, textTransform: 'uppercase', letterSpacing: 0.7 }}>{label}</span>
      <span style={{
        fontSize: 13, color: TEKST.primær, fontWeight: 500,
        fontFamily: mono ? FONT.mono : FONT.family,
        wordBreak: 'break-all',
      }}>{value}</span>
    </div>
  );

  const kpiData = agent.kpi_data && typeof agent.kpi_data === 'object'
    ? Object.entries(agent.kpi_data) : [];

  return (
    <ModalSkal åben={true} onLuk={onLuk}
      titel={agent.afd_navn || agent.display_navn || agent.agent_id} bredde={500}>
      {/* Status-oversigt */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20,
        padding: '12px 14px', background: 'rgba(255,255,255,0.03)',
        borderRadius: RADIUS.kort - 2, border: `1px solid ${KANT.fin}` }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: farve, flexShrink: 0,
          boxShadow: agent.er_aktiv ? `0 0 7px ${farve}` : 'none' }} />
        <StatusBadge status={badge} />
        <span style={{ fontSize: 12, color: TEKST.sekundær, marginLeft: 'auto' }}>
          Sidst aktiv: <strong style={{ color: TEKST.primær }}>{formatRelativTid(agent.sidst_aktiv)}</strong>
        </span>
      </div>

      {/* Seneste aktivitet */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 11, color: TEKST.dæmpet, textTransform: 'uppercase',
          letterSpacing: 0.7, marginBottom: 8 }}>Seneste aktivitet</div>
        <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: RADIUS.knap,
          border: `1px solid ${KANT.fin}`, padding: '12px 14px' }}>
          {agent.aktuel_opgave ? (
            <span style={{ fontSize: 13, color: TEKST.primær }}>{agent.aktuel_opgave}</span>
          ) : agent.status_tekst ? (
            <span style={{ fontSize: 13, color: TEKST.sekundær, fontStyle: 'italic' }}>{agent.status_tekst}</span>
          ) : (
            <span style={{ fontSize: 13, color: TEKST.dæmpet, fontStyle: 'italic' }}>Ingen registreret opgave</span>
          )}
        </div>
      </div>

      {/* Detalje-felter */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
        {felt('Agent-id', agent.agent_id, true)}
        {felt('Afdeling', agent.afd_navn || null)}
        {felt('Division', agent.division_navn ? `D${agent.division_nr} — ${agent.division_navn}` : null)}
        {felt('Sidst aktiv', agent.sidst_aktiv
          ? new Date(agent.sidst_aktiv).toLocaleString('da-DK', { dateStyle: 'short', timeStyle: 'short' })
          : 'Aldrig')}
        {agent.afventende_opgaver > 0 && felt('Afventende opgaver', agent.afventende_opgaver)}
        {agent.rapport_opdateret && felt('Rapport opdateret',
          new Date(agent.rapport_opdateret).toLocaleString('da-DK', { dateStyle: 'short', timeStyle: 'short' }))}
      </div>

      {/* KPI-data */}
      {kpiData.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: 11, color: TEKST.dæmpet, textTransform: 'uppercase',
            letterSpacing: 0.7, marginBottom: 10 }}>KPI-data</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
            {kpiData.map(([k, v]) => (
              <div key={k} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: RADIUS.knap,
                border: `1px solid ${KANT.fin}`, padding: '8px 12px' }}>
                <div style={{ fontSize: 10, color: TEKST.dæmpet, marginBottom: 3 }}>{k}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: TEKST.primær }}>
                  {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Forbedringsforslag */}
      {agent.forbedringsforslag && (
        <div style={{ marginTop: 16, padding: '10px 14px',
          background: 'rgba(245,158,11,0.06)', borderRadius: RADIUS.knap,
          border: '1px solid rgba(245,158,11,0.20)' }}>
          <div style={{ fontSize: 10, color: '#fbbf24', textTransform: 'uppercase',
            letterSpacing: 0.7, marginBottom: 6 }}>Forbedringsforslag</div>
          <div style={{ fontSize: 12, color: TEKST.sekundær }}>{agent.forbedringsforslag}</div>
        </div>
      )}

      {/* Statusforklaring */}
      <div style={{ marginTop: 20, padding: '10px 14px',
        background: 'rgba(255,255,255,0.02)', borderRadius: RADIUS.knap,
        border: `1px solid ${KANT.fin}`, fontSize: 11, color: TEKST.dæmpet, lineHeight: 1.6 }}>
        <strong style={{ color: TEKST.sekundær }}>Om agent-status:</strong>{' '}
        <span style={{ color: STATUS.live, fontWeight: 600 }}>Aktiv</span> = kørte inden for 24t
        (normalt for on-demand-agenter).{' '}
        <span style={{ color: STATUS.ikkeForbundet, fontWeight: 600 }}>Standby</span> = ikke kørt for nylig — ikke en fejl,
        blot ingen opgaver i perioden.{' '}
        <span style={{ color: STATUS.fejl, fontWeight: 600 }}>Fejl</span> = reel fejl-tilstand kræver opmærksomhed.
      </div>
    </ModalSkal>
  );
}

// ─── KPI-data viser ──────────────────────────────────────────────────────────

function KpiDataMini({ kpiData }) {
  if (!kpiData || typeof kpiData !== 'object') return null;
  const entries = Object.entries(kpiData).slice(0, 3);
  if (entries.length === 0) return null;
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 6 }}>
      {entries.map(([k, v]) => (
        <div key={k} style={{ fontSize: 11 }}>
          <span style={{ color: TEKST.dæmpet }}>{k}: </span>
          <span style={{ color: TEKST.sekundær, fontWeight: 600 }}>
            {typeof v === 'object' ? JSON.stringify(v) : String(v)}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Afdeling-række ───────────────────────────────────────────────────────────

function AfdelingRaekke({ agent, erSidste, onKlik }) {
  const farve  = agentStatusFarve(agent);
  const badge  = agentBadge(agent);
  const [hover, setHover] = useState(false);
  const harKpi = agent.kpi_data && typeof agent.kpi_data === 'object' && Object.keys(agent.kpi_data).length > 0;

  return (
    <div
      onClick={() => onKlik?.(agent)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding:       '10px 14px',
        borderBottom:  erSidste ? 'none' : `1px solid ${KANT.fin}`,
        background:    hover ? 'rgba(255,255,255,0.02)' : 'transparent',
        transition:    'background 0.12s',
        cursor:        'pointer',
      }}
    >
      {/* Hoved-række */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        {/* Venstre: navn + agent_id */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 6, height: 6, borderRadius: '50%',
              background: farve,
              flexShrink: 0,
              boxShadow: agent.er_aktiv ? `0 0 5px ${farve}` : 'none',
            }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: TEKST.primær }}>
              {agent.afd_navn || agent.display_navn || agent.agent_id}
            </span>
            <span style={{ fontSize: 10, color: TEKST.dæmpet, fontFamily: FONT.mono }}>
              /{agent.agent_id}
            </span>
          </div>
          {/* Aktuel opgave */}
          {agent.aktuel_opgave && (
            <div style={{ fontSize: 11, color: TEKST.sekundær, marginTop: 3, paddingLeft: 14,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 340 }}>
              {agent.aktuel_opgave}
            </div>
          )}
          {/* Status-tekst fra afdeling_status */}
          {agent.status_tekst && (
            <div style={{ fontSize: 11, color: TEKST.sekundær, marginTop: 2, paddingLeft: 14, fontStyle: 'italic' }}>
              {agent.status_tekst}
            </div>
          )}
          {/* KPI-data mini */}
          {harKpi && (
            <div style={{ paddingLeft: 14 }}>
              <KpiDataMini kpiData={agent.kpi_data} />
            </div>
          )}
        </div>

        {/* Højre: status + sidst aktiv */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
          <StatusBadge status={badge} lille />
          <span style={{ fontSize: 10, color: TEKST.dæmpet }}>
            {formatRelativTid(agent.sidst_aktiv)}
          </span>
          {agent.afventende_opgaver > 0 && (
            <span style={{ fontSize: 10, color: STATUS.opmærksomhed, fontWeight: 600 }}>
              {agent.afventende_opgaver} afventer
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Divisions-sektion ────────────────────────────────────────────────────────

function DivisionSektion({ divisionNavn, divisionNr, agenter, defaultAaben = false, onKlikAgent }) {
  const [aaben, setAaben] = useState(defaultAaben);
  const aktive  = agenter.filter(a => agentBadge(a) === 'AKTIV').length;
  const i_alt   = agenter.length;
  const harFejl = agenter.some(a => agentBadge(a) === 'FEJL_AGENT');

  return (
    <Boks style={{ marginBottom: 14, padding: 0, overflow: 'hidden' }}>
      {/* Division-header */}
      <button
        onClick={() => setAaben(p => !p)}
        style={{
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          padding: '14px 18px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          textAlign: 'left',
          fontFamily: FONT.family,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 10, color: TEKST.dæmpet, fontFamily: FONT.mono, minWidth: 18 }}>
            {divisionNr != null ? `D${divisionNr}` : '—'}
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: TEKST.primær }}>{divisionNavn}</span>
          <span style={{ fontSize: 11, color: TEKST.sekundær }}>
            ({i_alt} {i_alt === 1 ? 'agent' : 'agenter'})
          </span>
          {aktive > 0 && (
            <StatusBadge status="AKTIV" tekst={`${aktive} aktiv`} lille />
          )}
          {harFejl && (
            <StatusBadge status="FEJL_AGENT" tekst="fejl" lille />
          )}
        </div>
        <span style={{ fontSize: 12, color: TEKST.dæmpet, transform: aaben ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
          ▾
        </span>
      </button>

      {/* Agenter-liste */}
      {aaben && (
        <div style={{ borderTop: `1px solid ${KANT.fin}` }}>
          {agenter.map((a, i) => (
            <AfdelingRaekke
              key={a.agent_id}
              agent={a}
              erSidste={i === agenter.length - 1}
              onKlik={onKlikAgent}
            />
          ))}
        </div>
      )}
    </Boks>
  );
}

// ─── PANEL ────────────────────────────────────────────────────────────────────

export default function AfdelingerPanel() {
  // ── Data-fetching ──────────────────────────────────────────────────────────
  const {
    data:      afdelData,
    loading:   loading,
    opdateret: opdateret,
    refresh:   afdRefresh,
  } = useApiPolling('/os/hjerne/afdelinger', 60000);

  // ── State ──────────────────────────────────────────────────────────────────
  const [refreshing,  setRefreshing]  = useState(false);
  const [soeg,        setSoeg]        = useState('');
  const [valgtAgent,  setValgtAgent]  = useState(null);

  const refreshAlt = useCallback(async () => {
    setRefreshing(true);
    await afdRefresh();
    setRefreshing(false);
  }, [afdRefresh]);

  // ── Udled data ─────────────────────────────────────────────────────────────

  const alleAgenter = afdelData?.agenter || [];

  // Filtrér ved søgning
  const filtrerede = soeg.trim()
    ? alleAgenter.filter(a =>
        (a.display_navn || '').toLowerCase().includes(soeg.toLowerCase()) ||
        (a.afd_navn     || '').toLowerCase().includes(soeg.toLowerCase()) ||
        (a.division_navn|| '').toLowerCase().includes(soeg.toLowerCase()) ||
        (a.agent_id     || '').toLowerCase().includes(soeg.toLowerCase())
      )
    : alleAgenter;

  // Gruppér per division
  const divisionerMap = new Map();
  for (const a of filtrerede) {
    const key   = a.division_nr != null ? String(a.division_nr) : '__ingen__';
    const label = a.division_navn || (a.division_nr != null ? `Division ${a.division_nr}` : 'Øvrige');
    if (!divisionerMap.has(key)) {
      divisionerMap.set(key, { divisionNr: a.division_nr, divisionNavn: label, agenter: [] });
    }
    divisionerMap.get(key).agenter.push(a);
  }
  const divisioner = [...divisionerMap.values()]
    .sort((a, b) => {
      if (a.divisionNr == null) return 1;
      if (b.divisionNr == null) return -1;
      return a.divisionNr - b.divisionNr;
    });

  const antalDivisioner = divisioner.filter(d => d.divisionNr != null).length;
  const antalAgenter    = alleAgenter.length;
  const antalAktive     = alleAgenter.filter(a => agentBadge(a) === 'AKTIV').length;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ fontFamily: FONT.family }}>

      {/* Panel-header */}
      <SektionsHeader
        label="Afdelinger"
        opdateret={opdateret}
        onRefresh={refreshAlt}
        refreshing={refreshing}
        style={{ marginBottom: 20 }}
      />

      {/* ── KPI-række ───────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 22 }}>
        <KpiKort
          label="Divisioner"
          value={antalDivisioner || null}
          farve={STATUS.konfigureret}
          ikon="⬡"
          badge="LIVE"
          loading={loading}
        />
        <KpiKort
          label="Agenter / afdelinger"
          value={antalAgenter || null}
          farve={STATUS.ai}
          ikon="◉"
          badge="LIVE"
          loading={loading}
        />
        <KpiKort
          label="Aktive agenter"
          value={antalAktive}
          farve={antalAktive > 0 ? STATUS.live : STATUS.ikkeForbundet}
          ikon="▶"
          badge="LIVE"
          loading={loading}
        />
      </div>

      {/* ── Søg ─────────────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 18 }}>
        <input
          type="text"
          value={soeg}
          onChange={e => setSoeg(e.target.value)}
          placeholder="Søg afdeling, division, agent-id…"
          style={{
            width: '100%', maxWidth: 360, boxSizing: 'border-box',
            background: 'rgba(255,255,255,0.04)', border: `1px solid ${KANT.standard}`,
            borderRadius: RADIUS.knap, padding: '8px 14px',
            color: TEKST.primær, fontSize: 13, outline: 'none',
            fontFamily: FONT.family,
          }}
        />
        {soeg && (
          <span style={{ marginLeft: 10, fontSize: 12, color: TEKST.sekundær }}>
            {filtrerede.length} resultat{filtrerede.length !== 1 ? 'er' : ''}
          </span>
        )}
      </div>

      {/* ── Divisioner ──────────────────────────────────────────────────────── */}
      {loading ? (
        <Boks>
          <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter afdelinger…</div>
        </Boks>
      ) : alleAgenter.length === 0 ? (
        <TomTilstand
          titel="Ingen afdelinger fundet"
          besked="Data hentes fra os.hjerne_agent_status LEFT JOIN os.afdelinger via /api/os/hjerne/afdelinger. Verificér at tabellerne er populeret."
          ikon="⬡"
          badge="KONFIGURERET"
        />
      ) : divisioner.length === 0 ? (
        <TomTilstand
          titel="Ingen resultater for søgning"
          besked={`Ingen afdelinger matcher "${soeg}"`}
          ikon="◎"
          badge="LIVE"
        />
      ) : (
        divisioner.map((div, i) => (
          <DivisionSektion
            key={div.divisionNr ?? '__ingen__'}
            divisionNr={div.divisionNr}
            divisionNavn={div.divisionNavn}
            agenter={div.agenter}
            defaultAaben={i < 2}
            onKlikAgent={setValgtAgent}
          />
        ))
      )}

      {/* ── Afdeling-detalje modal ────────────────────────────────────────── */}
      {valgtAgent && (
        <AfdelingDetaljeModal
          agent={valgtAgent}
          onLuk={() => setValgtAgent(null)}
        />
      )}

      {/* Footer */}
      {!loading && alleAgenter.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 11, color: TEKST.dæmpet }}>
          Data: os.hjerne_agent_status · os.afdelinger · os.afdeling_status
          <StatusBadge status="LIVE" lille style={{ marginLeft: 8, verticalAlign: 'middle' }} />
        </div>
      )}
    </div>
  );
}
